import type { IdentifyResponse, RoofResult, RoofSearchResponse, SearchLocation } from "../react-app-env";
const ol = require('openlayers');

const API3_URL = process.env.REACT_APP_GEO_ADMIN_API_URL || '';
const WMTS_URL = process.env.REACT_APP_GEO_ADMIN_WMTS_URL || '';

// Fetch API wrapper for reusability
const fetchData = async (url: string) => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return await response.json();
    } catch (error) {
        console.error(`Error fetching from ${url}:`, error);
        throw error;
    }
};

// Get layers configuration
export const getLayersConfig = async (lang: string): Promise<Record<string, any>> => {
    const url = `${API3_URL}/rest/services/api/MapServer/layersConfig?lang=${lang}`;
    return await fetchData(url);
};

// Create WMTS Layer
export const getWmts = (layer: string, options: any): any => {
    const resolutions = options.resolutions || [
        4000, 3750, 3500, 3250, 3000, 2750, 2500, 2250, 2000, 1750, 1500, 1250,
        1000, 750, 650, 500, 250, 100, 50, 20, 10, 5, 2.5, 2, 1.5, 1, 0.5,
    ];

    const tileGrid = new ol.tilegrid.WMTS({
        origin: [420000, 350000],
        resolutions,
        matrixIds: resolutions.map((_: any, i: any) => i.toString()),
    });

    const extension = options.format || 'png';
    const timestamp = options.timestamp || options.timestamps?.[0] || 'current';
    const wmtsUrl = `${WMTS_URL}/1.0.0/${layer}/default/${timestamp}/21781/{TileMatrix}/{TileRow}/{TileCol}.${extension}`;

    return new ol.source.WMTS({
        url: wmtsUrl,
        crossOrigin: 'anonymous',
        attributions: options.attribution
            ? `<a href="${options.attributionUrl}" target="_blank">${options.attribution}</a>`
            : undefined,
        tileGrid,
        layer: options.serverLayerName || layer,
        requestEncoding: 'REST',
        dimensions: { Time: timestamp },
    });
};

// Fetch roof information
export const getRoofInformation = async () => {
    return await fetchData("url");
};

// Search locations
export const searchLocations = async (searchText: string, lang: string): Promise<SearchLocation[]> => {
    const url = `${API3_URL}/rest/services/api/SearchServer?lang=${lang}&searchText=${encodeURIComponent(searchText)}&type=locations`;

    try {
        const data = await fetchData(url);
        return data.results
            .map((item: any) => {
                let label = item.attrs?.label?.replace(/(\d*[a-zA-Z]*)(\s*<b>)/, "$1,&nbsp;$2");
                return ({
                    id: item.id,
                    weight: item.weight,
                    label: label,
                    x: item.attrs.x,
                    y: item.attrs.y,
                    origin: item.attrs.origin,
                    cleanLabel: label.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' '),
                });
            })
            .filter((f: any) => f.origin === 'address' || f.origin === 'parcel')
            .sort((a: any, b: any) => b.weight - a.weight || a.cleanLabel.localeCompare(b.cleanLabel));
    } catch (error) {
        return [];
    }
};

// Search features from coordinates
export const searchFeaturesFromCoord = async (
    map: any,
    coord: [number, number],
    mapExtent: any,
    pixelTolerance: number,
    lang: string
): Promise<IdentifyResponse> => {
    const baseParams = `geometryType=esriGeometryPoint&returnGeometry=true&layers=all:ch.bfe.solarenergie-eignung-daecher`;
    const imageDisplay = `${map.getSize()?.join(",")},96`;
    const baseUrl = `${API3_URL}/rest/services/api/MapServer/identify?${baseParams}&geometry=${coord.join(",")}&mapExtent=${mapExtent.join(",")}&imageDisplay=${imageDisplay}&tolerance=${pixelTolerance}&order=distance&lang=${lang}`;

    try {
        const data: IdentifyResponse = await fetchData(baseUrl);
        if (!data.results[0]) {
            const perimeterUrl = `${baseUrl}&mapExtent=411600,55800,891600,330550&tolerance=1`;
            const perimeterData: IdentifyResponse = await fetchData(perimeterUrl);
            return { results: [{ perimeter: Boolean(perimeterData.results[0]) }] };
        } else if (data.results[0].featureId === -999) {
            return { results: [{ perimeter: true }] };
        }
        return data;
    } catch {
        return { results: [] };
    }
};

// Search best roof by building ID
export const searchBestRoofFromBuildingId = async (buildingId: string): Promise<RoofResult | null> => {
    const url = `${API3_URL}/rest/services/api/MapServer/find?layer=ch.bfe.solarenergie-eignung-daecher&searchField=building_id&searchText=${buildingId}&contains=false`;

    try {
        const data: RoofSearchResponse = await fetchData(url);
        return data.results?.length
            ? data.results.reduce((bestRoof, roofCandidate) =>
                roofCandidate.attributes.mstrahlung > bestRoof.attributes.mstrahlung ? roofCandidate : bestRoof
            )
            : null;
    } catch {
        return null;
    }
};

// Search ALL roofs by building ID (for select-all on double-click)
export const searchAllRoofsFromBuildingId = async (buildingId: string): Promise<any[]> => {
    const url = `${API3_URL}/rest/services/api/MapServer/find?layer=ch.bfe.solarenergie-eignung-daecher&searchField=building_id&searchText=${buildingId}&contains=false&returnGeometry=true`;

    try {
        const data = await fetchData(url);
        return data.results || [];
    } catch {
        return [];
    }
};

// Search geocode by coordinates
export const searchGeocode = async (
    map: any,
    coords: number[],
    pixelTolerance: number,
    mapExtent: any
): Promise<any> => {
    const url = `${API3_URL}/rest/services/api/MapServer/identify?geometryType=esriGeometryPoint&geometry=${coords}&imageDisplay=${map.getSize()},96&mapExtent=${mapExtent}&tolerance=${pixelTolerance}&order=distance&lang=en&layers=all:ch.bfs.gebaeude_wohnungs_register&returnGeometry=true`;
    return await fetchData(url);
};
